import { useState, useMemo, useEffect } from 'react';
import { Box, Button, InputAdornment, TextField, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { GridColDef } from '@mui/x-data-grid';
import { atom, useSetAtom } from 'jotai';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import AddQuantity from './AddQuantity';
import { COLORS } from '../../../theme/themeConstants';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import ErrorHandler from '../../stateless/Common/ErrorHandler';
import { useGroupChoiceParams } from '../ManageChoiceGroup/useGroupChoiceParams';
import { useGetAllStocks } from '../../Query/StockQuery';
import { useDebounce } from '../../../utils/useDounce';
import { useTranslation } from 'react-i18next';

interface StockRow {
  itemId: string;
  itemName: string;
  productCode: string;
  restaurantName: string;
  merchantName: string;
  quantity: number;
  soldQuantity: number;
}

interface StockListProps {
  storeId?: string;
}

// type RowWithSno = StockRow & { id: string; sno: number };

export const StockAtom = atom<StockRow['itemId'] | ''>('');

const StockList: React.FC<StockListProps> = () => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  
  const setStockAtom = useSetAtom(StockAtom);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const { params, dispatch } = useGroupChoiceParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const { data: axiosResponse, isLoading, refetch } = useGetAllStocks(memoizedParams);

  const AllStock = axiosResponse?.data;

  const filteredRows = useMemo(() => {
    if (!AllStock?.data) return [];
    const storeOwners = AllStock?.data || []


    return storeOwners.map((row, index) => ({
      ...row,
      id: row.itemId,
      sno: index + 1 + (Number(params.page - 1)) * Number(params.limit)
    }));
  }, [params.search, AllStock, params.page, params.limit])




  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);


  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };


  const handleAddQuantity = (id: string) => {
    setOpenAddDialog(true);
    setStockAtom(id);
  };
  const closeAddDialog = () => setOpenAddDialog(false);

  const handleDownload = () => {
    // Filter rows to export based on selection
    const dataToExport = (selectedRows.length > 0 ?
      filteredRows.filter(row => selectedRows.includes(row.id)) :
      filteredRows
    ).map(({
      sno,
      productCode,
      itemName,
      restaurantName,
      merchantName,
      quantity,
      soldQuantity,
    }) => ({
      'S.No': sno,
      'Product Code': productCode,
      Item: itemName,
      'Store Name': restaurantName,
      'Merchant Name': merchantName,
      Quantity: quantity,
      'Sold Quantity': soldQuantity,
    }));

    if (dataToExport.length === 0) {
      alert(t('stockManagement.messages.noRowsToExport'));
      return;
    }

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stocks');
    XLSX.writeFile(wb, 'Stock_List.xlsx');
  };
  const columns = useMemo<GridColDef[]>(() => [
    { field: 'sno', headerName: t('stockManagement.columns.sno'), width: 80 },
    { field: 'productCode', headerName: t('stockManagement.columns.productCode'), flex: 1 },
    { field: 'itemName', headerName: t('stockManagement.columns.itemName'), flex: 1 },
    { field: 'restaurantName', headerName: t('stockManagement.columns.restaurantName'), flex: 0.8 },
    { field: 'merchantName', headerName: t('stockManagement.columns.merchantName'), flex: 0.8 },
    { field: 'quantity', headerName: t('stockManagement.columns.quantity'), flex: 0.8 },
    { field: 'soldQuantity', headerName: t('stockManagement.columns.soldQuantity'), flex: 0.8 },
    {
      field: 'actions',
      headerName: t('stockManagement.columns.actions'),
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('stockManagement.actions.addQuantity')} arrow>
            <Button
              sx={{ textTransform: 'none', color: COLORS.secondary.main }}
              onClick={() => handleAddQuantity(params.row.id)}
            >
              {t('stockManagement.actions.addQuantity')}
            </Button>
          </Tooltip>
        </Box>
      ),
    },
  ], [t, handleAddQuantity]);
  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          mt: 2.5,
          color: '#666',
        }}
      >
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder={t('stockManagement.searchPlaceholder')}
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none',
                },
                '& fieldset': { border: 'none' },
                '& .MuiInputBase-input': { pt: '10px' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              onClick={handleDownload}
              size="small"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: COLORS.primary.main,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: COLORS.primary.main,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              {t('stockManagement.actions.downloadList')}
            </Button>
          </Box>
        </Box>
        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={closeAddDialog}
          title={t('stockManagement.modal.addQuantity')}
        >
          <AddQuantity onCancel={closeAddDialog} />
        </EditStoreOwnerModal>
        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={AllStock?.pagination?.totalItems}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message={t('stockManagement.messages.noData')} onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
        />
      </Box>
    </>
  );
};

export default StockList;
